import type { CopilotSessionsSummary } from '@ace-tracker/shared';
import { SESSIONS_METRIC_INFO } from '../metricInfo';
import { TOOL_COLORS } from '../toolColors';
import { DeveloperName } from '../settings';
import { TableCard } from './TableCard';

const DASH = '—';

function formatDuration(minutes: number): string {
  if (minutes < 1) return '<1 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  return `${Math.floor(minutes / 60)} h ${Math.round(minutes % 60)} min`;
}

/**
 * GitHub Copilot sessions (E2.10): VS Code chat and Copilot CLI sessions from
 * collector 1.6.0+. No cost column — Copilot is not priced per token and stays
 * out of spend — so runaway is judged on duration and requests only.
 */
export function CopilotSessionsSection({
  copilot,
  SectionHeading,
}: {
  copilot: CopilotSessionsSummary;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  return (
    <section>
      <SectionHeading
        title="GitHub Copilot sessions"
        hint="VS Code chat and Copilot CLI · no cost: Copilot is not priced per token"
        info={SESSIONS_METRIC_INFO.copilot}
      />
      {copilot.sessions === 0 ? (
        <p className="rounded-card border border-border bg-surface p-5 text-sm text-text-muted shadow-sm">
          No GitHub Copilot session records in this range. They need collector 1.6.0 or later on developers' machines; until then
          Copilot appears on its own tab only.
        </p>
      ) : (
        <>
          <div className="relative mb-4 rounded-card border border-border bg-surface p-5 shadow-sm">
            <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card" style={{ backgroundColor: TOOL_COLORS.copilot }} />
            <dl className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm sm:grid-cols-4">
              <Metric label="Sessions" value={copilot.sessions.toLocaleString()} />
              <Metric label="Median requests" value={Math.round(copilot.medianRequests).toLocaleString()} />
              <Metric label="Median duration" value={formatDuration(copilot.medianDurationMinutes)} />
              <Metric label="Runaway" value={copilot.thresholds ? copilot.runaway.toLocaleString() : DASH} />
            </dl>
            <p className="mt-3 text-xs text-text-muted">
              {copilot.bySurface.map((s) => `${s.surface}: ${s.sessions} sessions, ${s.requests.toLocaleString()} requests`).join(' · ')}.{' '}
              {copilot.thresholds
                ? `Runaway above ${formatDuration(copilot.thresholds.durationMinutes)} or ${Math.round(copilot.thresholds.requests)} requests.`
                : 'Too few sessions to flag runaways.'}
            </p>
          </div>
          <TableCard
            title="Copilot sessions by requests"
            info={SESSIONS_METRIC_INFO.copilot}
            empty={copilot.list.length === 0}
            emptyText="No Copilot sessions in this range."
          >
            <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
              <tr>
                <th className="pb-2.5 pr-3">Session</th>
                <th className="pb-2.5 pr-3">Developer</th>
                <th className="pb-2.5 pr-3">Surface</th>
                <th className="pb-2.5 pr-3">Models</th>
                <th className="pb-2.5 pr-3">Started</th>
                <th className="pb-2.5 pr-3 text-right">Requests</th>
                <th className="pb-2.5 pr-3 text-right">Duration</th>
                <th className="pb-2.5">Runaway on</th>
              </tr>
            </thead>
            <tbody>
              {copilot.list.map((s) => (
                <tr key={s.sessionId} className="border-b border-border last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs text-text-muted">{s.sessionId.slice(0, 8)}</td>
                  <td className="py-2 pr-3 text-text-default">{s.developerId ? <DeveloperName developerId={s.developerId} /> : DASH}</td>
                  <td className="py-2 pr-3 text-text-muted">{s.surface}</td>
                  <td className="max-w-[220px] truncate py-2 pr-3 text-text-muted" title={s.models.join(', ') || undefined}>
                    {s.models.length > 0 ? s.models.join(', ') : DASH}
                  </td>
                  <td className="py-2 pr-3 tabular-nums text-text-muted">{new Date(s.firstSeen).toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{s.requests.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{formatDuration(s.durationMinutes)}</td>
                  <td className="py-2">
                    {s.runawayReasons.length === 0 ? (
                      <span className="text-text-faint">{DASH}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-input bg-warn-bg px-1.5 py-0.5 text-[11px] text-text-default">
                        <i aria-hidden="true" className="fa-solid fa-triangle-exclamation text-warn-ink" />
                        {s.runawayReasons.map((r) => (r === 'duration' ? 'Duration' : 'Requests')).join(', ')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableCard>
        </>
      )}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-text-faint">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-text-default">{value}</dd>
    </div>
  );
}
